/**
 * /moniteur/planning — Planning personnel autonome de l'enseignant.
 * Le moniteur crée ses propres événements (élèves, horaires, bilans),
 * indépendamment du planning centralisé secrétariat. Chaque cours peut
 * être partagé en fin de session via QR code (bilan + prochains cours).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Pencil, Share2, Clock, Calendar as CalIcon } from 'lucide-react';
import api from '../../api';
import PersonalEventModal from '../../components/PersonalEventModal';
import ShareQRModal from '../../components/ShareQRModal';

const TYPE_LABELS = {
  plateau: 'Plateau', circulation: 'Circulation', code: 'Code / Révision',
  salle: 'Cours en salle', autre: 'Autre',
};

export default function MoniteurPlanningPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [sharing, setSharing] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const r = await api.persoListEvents();
      setEvents(r?.items || []);
    } catch (_) { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    document.title = 'Mon planning · Espace enseignant';
    refresh();
  }, [refresh]);

  const openNew = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (ev) => { setEditing(ev); setModalOpen(true); };
  const openShare = (ev) => { setSharing(ev); setShareOpen(true); };

  // Grouper par date
  const grouped = events.reduce((acc, e) => {
    (acc[e.date] = acc[e.date] || []).push(e); return acc;
  }, {});
  const dates = Object.keys(grouped).sort();

  return (
    <div className="max-w-4xl" data-testid="moniteur-planning-page">
      <div className="flex items-start justify-between gap-3 mb-6">
        <div>
          <div className="text-[10.5px] font-black uppercase tracking-[0.22em] text-rose-500 mb-2">Enseignant · Planning</div>
          <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Ma journée de travail</h1>
          <p className="text-sm text-white/60 mt-2">Tes cours et rendez-vous avec tes élèves. En fin de séance : bouton “Partager” pour envoyer le bilan par QR code.</p>
        </div>
        <button onClick={openNew} data-testid="btn-add-perso-event" className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-black shadow-lg shadow-rose-900/30 transition">
          <Plus size={14} /> Nouveau cours
        </button>
      </div>

      {loading && <div className="text-white/40 text-sm">Chargement…</div>}

      {!loading && events.length === 0 && (
        <div className="rounded-2xl border border-dashed border-white/15 bg-white/[0.02] p-10 text-center" data-testid="planning-empty">
          <CalIcon className="w-8 h-8 text-white/30 mx-auto mb-2" />
          <div className="text-white/60 text-sm mb-1">Aucun cours planifié.</div>
          <div className="text-white/40 text-xs mb-4">Commence par ajouter un premier élève et un horaire.</div>
          <button onClick={openNew} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-black">
            <Plus size={14} /> Ajouter mon premier cours
          </button>
        </div>
      )}

      {!loading && dates.map(d => (
        <section key={d} className="mb-6">
          <h2 className="text-[10px] font-black uppercase tracking-widest text-rose-400 mb-2">
            {new Date(d).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
          </h2>
          <div className="space-y-2">
            {grouped[d].map(ev => (
              <div key={ev.id} className="rounded-xl border border-rose-500/20 bg-rose-500/[0.03] p-4 flex items-center gap-4" data-testid={`event-${ev.id}`}>
                <div className="w-1 h-12 rounded-full bg-rose-500" />
                <button onClick={() => openEdit(ev)} className="flex-1 text-left min-w-0">
                  <div className="text-white font-semibold text-sm truncate">{ev.title}</div>
                  <div className="text-white/60 text-xs mt-0.5 flex items-center gap-2 flex-wrap">
                    <Clock size={11} /> {(ev.start_time || '').slice(0, 5) || 'sans heure'} · {ev.duration_min} min
                    {ev.contact_name && ` · 👤 ${ev.contact_name}`}
                    {ev.location && ` · 📍 ${ev.location}`}
                  </div>
                </button>
                <div className="text-[10px] px-2 py-1 rounded-full bg-rose-500/15 text-rose-300 font-black uppercase tracking-widest">{TYPE_LABELS[ev.subject] || 'Cours'}</div>
                <button onClick={() => openShare(ev)} data-testid={`btn-share-${ev.id}`} className="px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/80 text-xs font-semibold inline-flex items-center gap-1" title="Partager le bilan">
                  <Share2 size={12} /> QR
                </button>
                <button onClick={() => openEdit(ev)} className="px-2 py-1.5 rounded-lg text-white/60 hover:bg-white/5" title="Modifier"><Pencil size={13} /></button>
              </div>
            ))}
          </div>
        </section>
      ))}

      <PersonalEventModal open={modalOpen} onClose={() => setModalOpen(false)} event={editing} mode="teacher" onSaved={refresh} />
      <ShareQRModal open={shareOpen} onClose={() => setShareOpen(false)} event={sharing} />
    </div>
  );
}
